import { createMemo, For, type JSX, Show } from 'solid-js';
import type { DashboardDetail, DashboardWidgetView } from '../lib/dashboards';
import { WidgetView } from './WidgetView';

// The renderer (arc 101 slice 09, task #81): draws a board from its JSON detail.
// The layout is a flat ordered flow of `{widgetId, span}` refs into the 12-col
// grid; CSS grid packs left-to-right and wraps on overflow, so no manual row
// math is needed — each cell just declares its column span. A ref whose widget is
// missing is skipped (the server builds layout + widgets together, so this can't
// normally happen — it's a defensive guard, not a code path).

const GRID_COLUMNS = 12;

function clampSpan(span: number): number {
  return Math.min(GRID_COLUMNS, Math.max(1, Math.round(span)));
}

export function DashboardRenderer(props: {
  detail: DashboardDetail;
  token: string | null;
}): JSX.Element {
  const byId = createMemo(() => new Map(props.detail.widgets.map((w) => [w.id, w] as const)));
  return (
    <div class="dashboard-grid">
      <For each={props.detail.layout.rows}>
        {(ref) => {
          const widget = (): DashboardWidgetView | undefined => byId().get(ref.widgetId);
          return (
            <Show when={widget()}>
              {(w) => (
                // Span rides a custom prop so CSS can force full-width below md
                // (an inline grid-column would beat the mobile collapse).
                <div class="dashboard-grid__cell" style={{ '--span': String(clampSpan(ref.span)) }}>
                  <WidgetView dashboardId={props.detail.id} widget={w()} token={props.token} />
                </div>
              )}
            </Show>
          );
        }}
      </For>
    </div>
  );
}
